import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePermissions } from '@/modules/common';
import { EntityIntegrationsRefreshCard } from '@/modules/common/components/organisms/entity-integrations-refresh-card';
import {
  BaseFeatureTab,
  BaseTabProps,
  FeatureMode,
  FeatureTabRef,
  useBaseTab,
} from '@/modules/common/components/templates/feature-details';
import { IntegrationInfoResponse } from '@/modules/common/types/dto/response/integration-response';
import { EntityType } from '@/modules/common/types/entity-type';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { SettingsRoutes } from '@/router/routes';
import { compareFormValues } from '@/utils/form-state.utils';
import { logger } from '@/utils/logger/logger';

import { ProjectIntegrationsTable } from '../../../components/project-details/integrations/project-integrations-table';
import { useProjectIntegrations } from '../../../hooks/use-project-integrations';
import { ProjectFormData } from '../../../types';
import { Project, ProjectStatus } from '../../../types/project';
import {
  ProjectIntegration,
  ProjectIntegrationsFormData,
} from '../../../types/project-integration';

const TAG = 'ProjectIntegrationsTab:';

export interface ProjectIntegrationsTabProps
  extends BaseTabProps<Project, ProjectIntegrationsFormData> {
  rawIntegrations?: IntegrationInfoResponse[];
}

export interface ProjectIntegrationsTabRef extends FeatureTabRef<ProjectFormData> {}

/**
 * Project integrations tab component supporting create, edit, and read modes.
 * Clean and simple implementation with proper data merging.
 */
export const ProjectIntegrationsTab = forwardRef<
  ProjectIntegrationsTabRef,
  ProjectIntegrationsTabProps
>((props, ref) => {
  const { mode, featureData: projectData, rawIntegrations, draftData } = props;

  const { t } = useTranslation(['authenticated.projects']);
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();

  const [integrations, setIntegrations] = useState<ProjectIntegration[]>([]);
  const [initialIntegrations, setInitialIntegrations] = useState<ProjectIntegration[]>([]);

  // Use base tab hook directly since this tab doesn't use a traditional form
  const { isReadMode } = useBaseTab(props, {
    isArchivedFn: project => project?.info.status === ProjectStatus.Archived,
    calculateDirtyStateFn: () => compareFormValues(integrations, initialIntegrations),
  });

  // Fetch available integrations
  // In Edit/Read mode, merge with selected integrations from project data
  // In Create mode, just fetch unlinked integrations
  const {
    data: availableIntegrationsData,
    isLoading,
    error,
  } = useProjectIntegrations({
    mode,
    projectId: projectData?.id,
    rawIntegrations: rawIntegrations,
  });

  // Build final integrations data based on mode
  const modeBasedIntegrations = useMemo(() => {
    if (!availableIntegrationsData) return [];

    logger.debug(TAG, 'Building final integrations', { mode });

    if (mode === FeatureMode.Create) {
      // Create mode: merge with draft data if available
      if (draftData?.integrations) {
        return availableIntegrationsData.map(integration => {
          const draftIntegration = draftData.integrations?.find(di => di.id === integration.id);
          return {
            ...integration,
            selectedValue: draftIntegration?.selectedValue || null,
          };
        });
      }
      // No draft data, return as-is (no selections)
      return availableIntegrationsData;
    }

    // Edit/Read mode: data is already merged from query
    return availableIntegrationsData;
  }, [availableIntegrationsData, mode, draftData?.integrations]);

  // Update integrations state when final data changes
  useEffect(() => {
    if (modeBasedIntegrations.length > 0) {
      setIntegrations(modeBasedIntegrations);
      setInitialIntegrations(modeBasedIntegrations);
    }
  }, [modeBasedIntegrations]);

  // Handle project selection change
  const handleProjectChange = useCallback((integrationId: string, externalProjectId?: string) => {
    logger.debug(TAG, 'Project selection changed', { integrationId, externalProjectId });

    setIntegrations(prev => {
      return prev.map(integration => {
        if (integration.id === integrationId) {
          let selectedValue: typeof integration.selectedValue = null;

          // Find the selected project from the integration's projects
          if (externalProjectId && integration.projects) {
            const selectedProject = integration.projects.find(p => p.id === externalProjectId);
            selectedValue = selectedProject || null;
          }

          return {
            ...integration,
            selectedValue,
          };
        }
        return integration;
      });
    });
  }, []);

  /**
   * Get form data for submission
   */
  const getFormData = (): ProjectFormData => {
    return {
      integrations,
    } as ProjectFormData;
  };

  /**
   * Custom submit handler for integrations
   */
  const submitHandler = async (): Promise<void> => {
    logger.debug(TAG, 'Form submission requested');
    // No validation needed for integrations
  };

  const { hasPermission } = usePermissions();
  const canManageIntegrations = hasPermission(OpsiumPolicy.IntegrationsManage);

  const tabContent = (
    <div className="space-y-4">
      {/* Main integrations card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('details.tabs.integrations.title')}</CardTitle>
            {canManageIntegrations && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open(SettingsRoutes.Integrations, '_blank')}
                disabled={isReadMode}
              >
                {t('details.tabs.integrations.manageIntegrations')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <EntityIntegrationsRefreshCard
            entityType={EntityType.Project}
            disabled={isReadMode}
            t={t}
            strings={{
              buttonTitle: t('details.tabs.integrations.refreshProjects'),
              buttonTitleRefreshing: t('details.tabs.integrations.refreshing'),
              descriptionI18nKey: 'details.tabs.integrations.refreshAlert',
              refreshButtonText: t('details.tabs.integrations.refreshButtonText'),
              successToast: t('details.tabs.integrations.refreshSuccess'),
              errorToast: t('details.tabs.integrations.refreshError'),
            }}
          />

          {/* Integrations table */}
          <ProjectIntegrationsTable
            integrations={integrations}
            isLoading={isLoading}
            error={error ? tCommon('error.generic') : null}
            isReadMode={isReadMode}
            onProjectChange={handleProjectChange}
          />
        </CardContent>
      </Card>
    </div>
  );

  return (
    <BaseFeatureTab
      {...props}
      ref={ref}
      isArchivedFn={project => project?.info.status === ProjectStatus.Archived}
      onSubmit={submitHandler}
      isValid={true}
      getFormData={getFormData}
    >
      {tabContent}
    </BaseFeatureTab>
  );
});

ProjectIntegrationsTab.displayName = 'ProjectIntegrationsTab';
